import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components-ui/Select';
import { Button } from '../../components-ui/Button';
import { cn } from '../../lib/utils';
import { useId } from '@radix-ui/react-id';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import React from 'react';

const DEFAULT_PAGE_SIZES = [10, 20, 30, 40, 50, 100, 500];

export const PaginationControls = ({
  gotoPage,
  canPreviousPage,
  canNextPage,
  pageCount,
  pageIndex,
  pageOptions,
  pageSize,
  setPageSize,
  nextPage,
  previousPage,
  showFirstPageButton = true,
  showLastPageButton = true,
  pageSizes = DEFAULT_PAGE_SIZES,
  className = '',
}) => {
  // The size picker is named by its visible caption and the size it shows ("Rows per page 25"). The size keeps its
  // place: space-x would push a span 10px off where the bare text sat.
  const sizeId = useId();
  // pb-* keeps the controls clear of the floating App Assistant launcher; the unset var
  // falls back to 0px, leaving py-3's 12px.
  return (
    <div className={cn('flex w-fit items-center justify-end gap-7 py-3 font-light', className)}>
      {/* Page size selection */}
      <div className="flex items-center space-x-2">
        <span id={`${sizeId}-caption`} className="text-sm">
          Rows per page
        </span>
        <Select value={pageSize} onValueChange={(value) => setPageSize(Number(value))}>
          <SelectTrigger
            aria-labelledby={`${sizeId}-caption ${sizeId}-value`}
            className="h-8 w-fit space-x-2.5 border-neutral-100 bg-white px-2 py-2 shadow-sm"
          >
            <SelectValue placeholder={`Show ${pageSize}`} />
            <span id={`${sizeId}-value`} className="!ml-0">
              {pageSize}
            </span>
          </SelectTrigger>
          <SelectContent>
            {pageSizes.map((size) => (
              <SelectItem className="flex justify-between px-2 py-1.5" key={size} value={size.toString()}>
                <span>Show</span> <span>{size}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {/* Page info */}
      <span className="text-sm">
        Page <strong>{pageIndex + 1}</strong> of {pageOptions.length}
      </span>
      <div className="flex gap-1.5">
        {/* First Page */}
        {showFirstPageButton && (
          <Button
            variant="outline"
            className="h-8 px-2 py-2"
            aria-label="First page"
            onClick={() => gotoPage(0)}
            disabled={!canPreviousPage}
          >
            <ChevronsLeft className="size-4 text-neutral-800" />
          </Button>
        )}

        {/* Previous Page */}
        <Button
          variant="outline"
          className="h-8 px-2 py-2"
          aria-label="Previous page"
          onClick={previousPage}
          disabled={!canPreviousPage}
        >
          <ChevronLeft className="size-4 text-neutral-800" />
        </Button>

        {/* Next Page */}
        <Button variant="outline" className="h-8 px-2 py-2" aria-label="Next page" onClick={nextPage} disabled={!canNextPage}>
          <ChevronRight className="size-4 text-neutral-800" />
        </Button>

        {/* Last Page */}
        {showLastPageButton && (
          <Button
            variant="outline"
            className="h-8 px-2 py-2"
            aria-label="Last page"
            onClick={() => gotoPage(pageCount - 1)}
            disabled={!canNextPage}
          >
            <ChevronsRight className="size-4 text-neutral-800" />
          </Button>
        )}
      </div>
    </div>
  );
};
